import { Button, DatePicker, Form, Input, Modal, Select, Switch } from "antd";
import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { AiOutlineEdit } from "react-icons/ai";
import { toast } from "react-toastify";

const { Option } = Select;

const searchFilter = (input, option) =>
  String(option?.children ?? "")
    .toLowerCase()
    .includes(input.trim().toLowerCase());

const Section = ({ title, children }) => (
  <div className='mb-6'>
    <h3 className='mb-3 border-b border-slate-200 pb-2 text-xs font-bold uppercase tracking-wider text-[#023f88]'>
      {title}
    </h3>
    <div className='grid gap-x-4 sm:grid-cols-2'>{children}</div>
  </div>
);

export default function EditUserModal({ user, roles, departments, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        ...user,
        usertype: user.usertype ?? undefined,
        departmentId: user.departmentId ?? undefined,
        dateOfBirth: user.dateOfBirth ? moment(user.dateOfBirth) : undefined,
        status: !!user.status,
        resetPassword: undefined,
        confirmPassword: undefined,
      });
    }
  }, [user, form]);

  const onFinish = async ({ confirmPassword, resetPassword, ...values }) => {
    setSaving(true);
    try {
      await axios.put(`customer/${user.id}`, {
        ...values,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : null,
        departmentId: values.departmentId ?? null,
        usertype: values.usertype ?? null,
        ...(resetPassword ? { resetPassword } : {}),
      });
      toast.success("User updated successfully");
      onSaved();
    } catch (error) {
      const message = error.response?.data?.message;
      if (message === "Email already registered") {
        form.setFields([{ name: "email", errors: ["This email is already registered"] }]);
      } else {
        toast.error(message || "Error updating user, try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const fullName = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "";

  return (
    <Modal
      open={!!user}
      onCancel={onClose}
      width={760}
      destroyOnClose
      centered
      footer={null}
      title={
        <div className='flex items-center gap-3'>
          <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#023f88] to-[#0b5cae] text-white'>
            <AiOutlineEdit className='text-xl' />
          </div>
          <div>
            <p className='text-lg font-bold text-slate-900'>Edit user</p>
            <p className='text-sm font-normal text-slate-600'>
              {fullName || user?.email}
            </p>
          </div>
        </div>
      }
    >
      <Form
        form={form}
        layout='vertical'
        className='cp-searchable-form max-h-[65vh] overflow-y-auto pr-1 pt-2'
        requiredMark={false}
        onFinish={onFinish}
        autoComplete='off'
      >
        <Section title='Account & access'>
          <Form.Item
            label='Email'
            name='email'
            rules={[
              { required: true, message: "Please input email!" },
              { type: "email", message: "Please enter a valid email!" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item label='Role' name='roleId'>
            <Select showSearch filterOption={searchFilter} placeholder='Select role'>
              {roles.map((role) => (
                <Option key={role.id} value={role.id}>
                  {role.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label='User type' name='usertype'>
            <Select
              showSearch
              allowClear
              filterOption={searchFilter}
              placeholder='Type or select user type'
            >
              <Option value={1}>Department Head</Option>
              <Option value={2}>Coordinator</Option>
            </Select>
          </Form.Item>
          <Form.Item label='Department' name='departmentId'>
            <Select
              showSearch
              allowClear
              filterOption={searchFilter}
              placeholder='Type or select department'
            >
              {departments.map((department) => (
                <Option key={department.id} value={department.id}>
                  {department.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label='Active' name='status' valuePropName='checked'>
            <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
          </Form.Item>
        </Section>

        <Section title='Personal details'>
          <Form.Item label='First name' name='firstName'>
            <Input />
          </Form.Item>
          <Form.Item label='Last name' name='lastName'>
            <Input />
          </Form.Item>
          <Form.Item label='Phone' name='phone'>
            <Input />
          </Form.Item>
          <Form.Item label='Date of birth' name='dateOfBirth'>
            <DatePicker className='w-full' />
          </Form.Item>
          <Form.Item label='Job title' name='jobTitle'>
            <Input />
          </Form.Item>
          <Form.Item label='Social media URL' name='socialMediaUrl'>
            <Input />
          </Form.Item>
        </Section>

        <Section title='Address'>
          <Form.Item label='Address' name='address' className='sm:col-span-2'>
            <Input />
          </Form.Item>
          <Form.Item label='City' name='city'>
            <Input />
          </Form.Item>
          <Form.Item label='State' name='state'>
            <Input />
          </Form.Item>
          <Form.Item label='Zip' name='zip'>
            <Input />
          </Form.Item>
          <Form.Item label='Country' name='country'>
            <Input />
          </Form.Item>
        </Section>

        <Section title='Security'>
          <Form.Item
            label='New password'
            name='resetPassword'
            extra='Leave empty to keep the current password.'
            rules={[{ min: 6, message: "Password must be at least 6 characters" }]}
          >
            <Input.Password autoComplete='new-password' />
          </Form.Item>
          <Form.Item
            label='Confirm new password'
            name='confirmPassword'
            dependencies={["resetPassword"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if ((value || "") === (getFieldValue("resetPassword") || "")) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password autoComplete='new-password' />
          </Form.Item>
        </Section>

        <div className='sticky bottom-0 flex justify-end gap-3 border-t border-slate-200 bg-white pt-4'>
          <Button onClick={onClose} className='rounded-lg'>
            Cancel
          </Button>
          <Button
            type='primary'
            htmlType='submit'
            loading={saving}
            className='cp-navy-btn rounded-lg'
          >
            Save changes
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
